import { TranslationFunction } from 'i18next';
import * as React from 'react';
import { translate } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import * as url from 'url';
import AppInsightsService from './app-insights/service';
import ModalDialog, { ModalBody, ModalHeader } from './components/modal-dialog';
import { LANGUAGE_CODE_EN_CA, LANGUAGE_CODE_FR_CA, ROUTE_PATH_VIDEO } from './constants';
import { default as i18n, getDocumentTitle } from './locales';
import { FaqQuestionGroup } from './locales/types';
import {  MIXPANEL_PROPERTY_PAGE_TITLE } from './mixpanel.service';
import './scss/faq-page.scss';
import './scss/static-content-page.scss';

interface FaqPageIndexComponentProps {
  show?: boolean;
  t: TranslationFunction;
  children?: void;
}

function FaqPageIndexComponent({ t, show }: FaqPageIndexComponentProps): React.ReactElement<FaqPageIndexComponentProps> | null {
  if (show === false) {
    return null;
  }

  const questionGroup = t('questionGroup') as FaqQuestionGroup;
  // let start = 1;
  const faqIndex = Object.keys(questionGroup).map((groupKey) => {
    const group = questionGroup[groupKey];
    const groupId = groupKey.toLowerCase();
    return (
      <div key={groupKey}>
        {group.title != '' && <a href={`#${groupId}`} dangerouslySetInnerHTML={{ __html: group.title }} />}
        {/* <ol start={start}> */}
        <ul>
          {group.list.map((item, index) => {
            // ++start;

            const trimmedItemKey = typeof item.key === 'string' ? item.key.trim() : '';
            const key = trimmedItemKey || `${groupId}q${index + 1}`;
            return (
              <li key={key}>
                <a href={`#${key}`} dangerouslySetInnerHTML={{ __html: item.question }} />
              </li>
            );
          })}
          {/* </ol> */}
        </ul>
      </div>
    );
  });

  return <section className="faq-index page-content">{faqIndex}</section>;
}

const FaqPageIndex = translate('faqPage', { i18n })(FaqPageIndexComponent);

interface FaqPageComponentProps extends RouteComponentProps<{}> {
  t: TranslationFunction;
  children?: void;
}

interface FaqPageComponentState {
  playVideoModalOpen: boolean;
}

class FaqPageComponent extends React.PureComponent<FaqPageComponentProps, FaqPageComponentState> {
  public state = {
    playVideoModalOpen: false
  };

  public constructor(props: FaqPageComponentProps) {
    super(props);

    this.showPlayVideoModal = this.showPlayVideoModal.bind(this);
    this.hidePlayVideoModal = this.hidePlayVideoModal.bind(this);
    this.toggleVideoTranscript = this.toggleVideoTranscript.bind(this);
    this.addVideoTranscriptEventHook = this.addVideoTranscriptEventHook.bind(this);
  }

  public componentDidMount(): void {
    // jump to hash link once render() is complete
    if (window.location.hash) {
      const elBookmark = document.querySelector(window.location.hash);
      if (elBookmark != null) {
        // @ts-ignore Type '{ behavior: "instant"; block: "start"; }' is not assignable to type 'ScrollIntoViewOptions'
        setTimeout(() => elBookmark.scrollIntoView({ behavior: 'instant', block: 'start' }));
      }
    }

    const allRouterLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[data-router-link]'));
    allRouterLinks.forEach((link) =>
      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const elLink = event.currentTarget as HTMLAnchorElement;
        const path = url.parse(elLink != null ? elLink.href : '').pathname;
        if (path != null) {
          if (path === ROUTE_PATH_VIDEO) {
            this.showPlayVideoModal();
          } else {
            this.props.history.push(path);
          }
        }
      })
    );
  }

  public render(): React.ReactNode {
    // let start = 1;
    const questionGroup = this.props.t('questionGroup') as FaqQuestionGroup;
    return (
      <React.Fragment>
        <article className="page-faq">
          <FaqPageIndex />

          {Object.keys(questionGroup).map((groupKey) => {
            const group = questionGroup[groupKey];
            const groupId = groupKey.toLowerCase();
            return (
              <section key={groupKey} id={groupId} className="page-content">
                {group.title !='' && <h1 dangerouslySetInnerHTML={{ __html: group.title }} />}
                {/* <ol start={start}> */}
                <ul className="faq">
                  {group.list.map((item, index) => {
                    // ++start;

                    const trimmedItemKey = typeof item.key === 'string' ? item.key.trim() : '';
                    const key = trimmedItemKey || `${groupId}q${index + 1}`;
                    return (
                      <li key={key} id={key} className="question">
                        <strong dangerouslySetInnerHTML={{ __html: item.question }} />
                        <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                      </li>
                    );
                  })}
                  {/* </ol> */}
                </ul>
              </section>
            );
          })}

          <section className="page-content section-postface" dangerouslySetInnerHTML={{ __html: this.props.t('postface') }} />
        </article>

        {this.renderVideoTutorialModal()}
      </React.Fragment>
    );
  }

  public renderVideoTutorialModal(): React.ReactNode {
    const videoUrlEnCa = 'https://player.vimeo.com/video/314809752';
    const videoUrlFrCa = 'https://player.vimeo.com/video/314812230';
    // const videoUrl = this.props.t('productVideo:videoUrl') as string;
    const videoUrl = i18n.language === LANGUAGE_CODE_FR_CA ? videoUrlFrCa : videoUrlEnCa;
    // const transcript = this.props.t('productVideo:transcript') as { [key: string]: string };
    const iFrameTitle =
      i18n.language === LANGUAGE_CODE_FR_CA ? 'EnGarde de Sigma Loyalty Group sur Vimeo' : 'OnGuard from Sigma Loyalty Group on Vimeo';

    return (
      <ModalDialog id="modal-product-video" size="large" show={this.state.playVideoModalOpen} onHidden={this.hidePlayVideoModal}>
        <ModalHeader>
          <button type="button" className="close" data-dismiss="modal" aria-label={this.props.t('global:modalDismissActionText')}>
            <span aria-hidden="true">&times;</span>
          </button>
        </ModalHeader>
        <ModalBody>
          <div className="embed-responsive embed-responsive-16by9">
            <iframe title={iFrameTitle} src={videoUrl} frameBorder={0} allowFullScreen={true} />
          </div>

          {/* <div className="accordion accordion-video-transcript" role="tablist">
            <div className="accordion__item">
              <div
                id="accordion__title-0"
                className="accordion__title btn-link"
                role="tab"
                tabIndex={0}
                aria-controls="accordion__body-0"
                aria-selected="false"
              >
                {transcript.toggleLabel}
              </div>
              <div
                id="accordion__body-0"
                className="accordion__body accordion__body--hidden"
                role="tabpanel"
                aria-labelledby="accordion__title-0"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: transcript.content }}
              />
            </div>
          </div> */}
        </ModalBody>
      </ModalDialog>
    );
  }

  public showPlayVideoModal(): void {
    this.setState(({ playVideoModalOpen }) => ({ playVideoModalOpen: !playVideoModalOpen }), this.addVideoTranscriptEventHook);
    this.trackEvent('Click Video Tutorial link');
  }

  public hidePlayVideoModal(): void {
    let restoreFocus = false;
    this.setState(
      ({ playVideoModalOpen }) => {
        restoreFocus = playVideoModalOpen;
        return { playVideoModalOpen: !playVideoModalOpen };
      },
      () => {
        if (restoreFocus) {
          const elWatchVideoAction = document.querySelector<HTMLAnchorElement>(`a[data-router-link][href="${ROUTE_PATH_VIDEO}"]`);
          if (elWatchVideoAction != null) {
            elWatchVideoAction.focus();
          }
        }
      }
    );
  }

  public addVideoTranscriptEventHook(): void {
    const elVideoTranscriptToggle = document.querySelector<HTMLElement>(`.accordion-video-transcript > .accordion__item`);
    if (elVideoTranscriptToggle !== null) {
      elVideoTranscriptToggle.addEventListener('click', this.toggleVideoTranscript);
    }
  }

  public toggleVideoTranscript(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const elCurrentTarget = event.currentTarget as HTMLElement;
    const elItemTitle = elCurrentTarget.querySelector<HTMLDivElement>('.accordion__title');
    const elItemBody = elCurrentTarget.querySelector<HTMLDivElement>('.accordion__body');
    if (elItemTitle && elItemBody) {
      if (event.target !== elItemTitle) {
        // XXX do not collapse if clicked inside of accordion item body
        return;
      }
      const expanded = elItemTitle.getAttribute('aria-selected') === 'true';
      if (expanded) {
        elItemTitle.setAttribute('aria-selected', 'false');
        elItemBody.setAttribute('aria-hidden', 'true');
        elItemBody.classList.add('accordion__body--hidden');
        this.trackEvent('Collapse Video Transcript');
      } else {
        elItemTitle.setAttribute('aria-selected', 'true');
        elItemBody.setAttribute('aria-hidden', 'false');
        elItemBody.classList.remove('accordion__body--hidden');
        this.trackEvent('Expand Video Transcript');
      }
    }
  }

  // tslint:disable-next-line:no-any
  private trackEvent(eventName: string, props?: { [key: string]: any }): void {
    const pageTitle = getDocumentTitle(window.location.pathname, { lng: LANGUAGE_CODE_EN_CA });
    const properties = Object.assign({}, { [MIXPANEL_PROPERTY_PAGE_TITLE]: pageTitle }, props);
    //MixpanelService.track(eventName, properties);
    AppInsightsService.trackEvent({ name: eventName, properties });
  }
}

const FaqPage = translate('faqPage', { i18n })(FaqPageComponent);
export default withRouter(FaqPage);
